import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import {runInAction} from 'mobx';
import {Observer} from 'mobx-react';

import type {CourseApiModel} from '@yourcoach/shared/api/course';
import type {ApiRpcQuery} from '@yourcoach/shared/api/utils/apiRequest';

import {useI18n} from '@yourcoach/i18n';
import {type GoalApiModel, goalStore} from '@yourcoach/shared/api/goal';
import {apiRequest} from '@yourcoach/shared/api/utils/apiRequest';
import {alert} from '@yourcoach/shared/components/alert';
import {getUuid} from '@yourcoach/shared/utils/getUuid';
import {logger} from '@yourcoach/shared/utils/logger';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';
import {
  getDescriptionSchema,
  getTitleSchema,
  validateFields,
} from '@yourcoach/shared/utils/validation';
import {createHtmlInputField} from '@yourcoach/shared/utils/validation/createHtmlInputField';

import type {Expanded as GoalExpanded} from '@src/modules/Goals/utils';
import type {GoalT as ProgramGoalT} from '@src/pages/CRUProgram/Goals';

import Button from '@src/components/Button';
import Loader from '@src/components/Loader/Loader';
import Switch from '@src/components/Switch';
import TextArea from '@src/components/TextArea';
import TextField from '@src/components/TextField';
import useIsVisible from '@src/hooks/useIsVisible';
import {expand as goalExpand} from '@src/modules/Goals/utils';

import styles from './CRUGoal.module.css';

const expand = JSON.parse(JSON.stringify(goalExpand)) as typeof goalExpand;

expand.goal!.push('course_id');

export type Props = {
  courseId?: null | string;
  goal?: GoalApiModel | null | ProgramGoalT;
  /**
   * Callback for usage with modal. Use for modal.hide etc.
   */
  onAfterCreateCb?: () => any;
  onAfterUpdateCb?: () => any;
  /**
   * Callback for custom update logic. Prevents default behaviour
   * @param goal
   */
  onSuccess?: (goal: ProgramGoalT) => void;
  programId?: null | string;
};

type GoalT = GoalApiModel &
  GoalExpanded & {
    course?: CourseApiModel | null;
  };

export const CRUGoal: FC<Props> = ({
  courseId,
  goal,
  onAfterCreateCb,
  onAfterUpdateCb,
  onSuccess,
  programId,
}) => {
  const {t} = useI18n();

  const [isCreatingMode, setIsCreatingMode] = useState(true);
  const [goalsAreExist, setGoalsAreExist] = useState(false);
  const [isClientManaged, setIsClientManaged] = useState(false);

  const [overlayIsVisible, showOverlay, hideOverlay] = useIsVisible();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const seeGoalsPartIsVisible = useMemo(
    () => isCreatingMode && goalsAreExist,
    [goalsAreExist, isCreatingMode],
  );

  const fields = useRef({
    description: createHtmlInputField<HTMLTextAreaElement>('description', {
      validationRule: getDescriptionSchema(),
    }),
    title: createHtmlInputField('title', {
      validationRule: getTitleSchema(),
    }),
  }).current;

  const fetchGoalsCount = useCallback(async () => {
    try {
      const result = await apiRequest({
        method: 'coach.goals.unbound.count',
        params: {
          query: (
            [
              programId ? ['program_id', '!=', programId] : null,
              ['course_id', '==', null],
            ] as ApiRpcQuery[]
          ).filter(Boolean),
        },
      });

      setGoalsAreExist(result._count > 0);

      return result._count;
    } catch (error) {
      logger.error(error);
    }
  }, [programId]);

  useEffect(() => {
    if (goal) {
      runInAction(() => {
        fields.title.value = goal.title;
        fields.description.value = goal.description;
      });

      setIsClientManaged((goal as GoalApiModel).is_client_managed);
      setIsCreatingMode(false);
    }

    fetchGoalsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onIsClientManagedChange = useCallback((value: boolean) => {
    setIsClientManaged(value);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSeeGoalsButtonClick = useCallback(() => {
    // TODO: Select previously created goals
  }, []);

  const createGoal = useCallback(async () => {
    try {
      showOverlay();

      if (programId) {
        const createdGoal = (await goalStore.create({
          course_id: courseId || null,
          description: fields.description.value,
          expand,
          is_client_managed: isClientManaged,
          program_id: programId,
          title: fields.title.value,
        })) as GoalT;

        if (
          createdGoal.course &&
          createdGoal.course.status === 'ongoing' &&
          createdGoal.course_id
        ) {
          await goalStore.coach.bound.synchronize({
            course_id: createdGoal.course_id,
          });
        }
      }

      onAfterCreateCb?.();
      hideOverlay();
    } catch (error) {
      hideOverlay();

      logger.error(error);

      alert.error(error);
    }
  }, [
    courseId,
    fields.description.value,
    fields.title.value,
    hideOverlay,
    isClientManaged,
    programId,
    showOverlay,
    onAfterCreateCb,
  ]);

  const updateGoal = useCallback(async () => {
    try {
      showOverlay();

      const data = {
        description: fields.description.value,
        expand,
        is_client_managed: isClientManaged,
        title: fields.title.value,
      };

      const updatedGoal = (await goalStore.update(
        goal as GoalApiModel,
        data,
      )) as GoalT;

      if (
        updatedGoal.course &&
        updatedGoal.course.status === 'ongoing' &&
        updatedGoal.course_id
      ) {
        await goalStore.coach.bound.synchronize({
          course_id: updatedGoal.course_id,
        });
      }

      onAfterUpdateCb?.();
      hideOverlay();
    } catch (error) {
      hideOverlay();

      logger.error(error);

      alert.error(error);
    }
  }, [
    fields.description.value,
    fields.title.value,
    goal,
    hideOverlay,
    isClientManaged,
    onAfterUpdateCb,
    showOverlay,
  ]);

  const onFooterButtonPress = useCallback(async () => {
    const fieldsAreValid = validateFields(fields);

    if (fieldsAreValid) {
      if (onSuccess) {
        const uuid = getUuid();

        onSuccess({
          ...goal,
          _id: goal && goal._id ? goal._id : uuid,
          description: fields.description.value,
          is_client_managed: isClientManaged,
          title: fields.title.value,
        });

        return;
      }

      if (isCreatingMode) {
        createGoal();
      } else {
        updateGoal();
      }
    }
  }, [
    createGoal,
    fields,
    goal,
    isClientManaged,
    isCreatingMode,
    onSuccess,
    updateGoal,
  ]);

  return (
    <div className={classNames('CRUGoal', styles.Component)}>
      <div className={styles.dataRow}>
        <h3 className={styles.header}>
          {t('web-app:CRUGoal.title_label')}
          {':'}
        </h3>
        <Observer>
          {() => (
            <TextField
              data-test-id={TestID.create_program_goals.input.title}
              error={fields.title.error || ''}
              onChange={fields.title.onChange}
              placeholder={t('web-app:CRUGoal.title_text_input_placeholder')}
              ref={fields.title.ref}
              value={fields.title.value}
            />
          )}
        </Observer>
      </div>
      <div className={classNames(styles.dataRow, styles.switchContainer)}>
        <div className={styles.switchContent}>
          <h3 className={styles.header}>
            {t('web-app:CRUGoal.is_client_managed_label')}
            {':'}
          </h3>
          <p className={styles.switchDescription}>
            {t('web-app:CRUGoal.is_client_managed_description')}
          </p>
        </div>
        <Switch isOn={isClientManaged} onChange={onIsClientManagedChange} />
      </div>
      <div className={styles.dataRow}>
        <h3 className={styles.header}>
          {t('web-app:CRUGoal.description_label')}
          {':'}
        </h3>
        <Observer>
          {() => (
            <TextArea
              className={styles.descriptionTextArea}
              data-test-id={TestID.create_program_goals.input.description}
              error={fields.description.error || ''}
              onChange={fields.description.onChange}
              placeholder={t(
                'web-app:CRUGoal.description_text_input_placeholder',
              )}
              ref={fields.description.ref}
              value={fields.description.value}
            />
          )}
        </Observer>
      </div>
      <div className={styles.footer}>
        <Button
          data-test-id={TestID.create_program_goals.button.create}
          onClick={onFooterButtonPress}
        >
          {goal ? t('shared:save') : t('shared:create')}
        </Button>
      </div>
      {overlayIsVisible ? (
        <div className="overlay">
          <Loader />
        </div>
      ) : null}
    </div>
  );
};
